import PropTypes from 'prop-types';
import React from 'react';
import ViewContainer from '@loggi/ui/components/view-container';
import InputLabel from '@loggi/ui/components/input-label';
import InputStatus from '@loggi/ui/components/input-status';
import Col from '@loggi/ui/components/col';
import Display from '@loggi/ui/components/display';
import Icon from '@loggi/ui/components/icon';
import inputSelectRange from '@loggi/ui/components/utils/dom/input-select-range';
import styles from '../styles.scss';
import classNames from 'classnames';
import { STATUS, STATUS_LIST } from '@loggi/ui/components/utils/colors/status';
import {
  SIZES,
  LARGE_SIZE,
  SMALL_SIZE
} from '@loggi/ui/components/utils/sizes';

const mapperIcons = {
  [STATUS.ACTIVE]: 'confirmation',
  [STATUS.ERROR]: 'delete',
  [STATUS.WARNING]: 'alert'
};

const ENTER_KEY_CODE = 13;

class TextInput extends React.Component {
  static displayName = 'TextInput';

  static propTypes = {
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyup: PropTypes.func,
    onEnter: PropTypes.func,
    type: PropTypes.string,
    helperText: PropTypes.string,
    value: PropTypes.string,
    displayStatus: PropTypes.bool,
    displayIcon: PropTypes.bool,
    statusText: PropTypes.string,
    statusInfo: PropTypes.element,
    hasFocus: PropTypes.bool,
    focus: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        start: PropTypes.number,
        end: PropTypes.number
      })
    ]),
    icons: PropTypes.shape({
      [STATUS.ACTIVE]: PropTypes.string,
      [STATUS.ERROR]: PropTypes.string,
      [STATUS.WARNING]: PropTypes.string
    }),
    inputProps: PropTypes.object,
    status: PropTypes.oneOf(STATUS_LIST),
    size: PropTypes.oneOf(SIZES),
    disableBorder: PropTypes.bool,
    inverse: PropTypes.bool,
    cursorAlwaysOnEnd: PropTypes.bool
  };

  static defaultProps = {
    type: 'text',
    disabled: false,
    value: '',
    icons: {},
    inputProps: {},
    displayStatus: true,
    displayIcon: true,
    disableBorder: false,
    cursorAlwaysOnEnd: false,
    onEnter: null
  };

  constructor(props, context) {
    super(props, context);

    this._handleOnChange = this._handleOnChange.bind(this);
    this._handleOnFocus = this._handleOnFocus.bind(this);
    this._handleOnBlur = this._handleOnBlur.bind(this);
    this._handleOnEnter = this._handleOnEnter.bind(this);
    this._handleOnKeyUp = this._handleOnKeyUp.bind(this);
    this.getInput = this.getInput.bind(this);
    this._focus = this._focus.bind(this);

    this.textInputRef = React.createRef();

    this.state = {
      value: props.value || '',
      hasFocus: false
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.value === nextState.value) {
      return true;
    }

    const { value } = nextProps;
    this.setState({ value });

    return false;
  }

  componentDidUpdate() {
    this._focus();
  }

  componentDidMount() {
    this._focus(true);
  }

  getInput() {
    return this.textInputRef.current;
  }

  _focus(mount) {
    const input = this.getInput();
    let focus = this.props.inputProps.focus || this.props.focus;
    if (mount && this.props.hasFocus) {
      focus = true;
    }

    if (focus && input !== null) {
      if (typeof focus === 'object') {
        let start = focus.start;
        let end = focus.end;

        if (start < 0 || end < 0) {
          input.focus();
        } else {
          inputSelectRange(input, start, end);
        }
      } else {
        input.focus();
      }
    }
  }

  _handleOnKeyUp(e) {
    const { cursorAlwaysOnEnd, onEnter } = this.props;
    const input = e.target;
    const valueLen = input.value.length;

    if (cursorAlwaysOnEnd) {
      input.setSelectionRange(valueLen, valueLen);
    }

    if (e.keyCode === ENTER_KEY_CODE && onEnter) {
      this._handleOnEnter();
    }
  }

  _handleOnChange(e) {
    e.preventDefault();

    const { onChange, disabled } = this.props;

    if (disabled) {
      return;
    }

    const value = e.target.value;

    this.setState({ value });

    if (onChange) {
      onChange(value);
    }
  }

  _handleOnFocus(e) {
    const {
      onFocus,
      inputProps: { onFocus: onFocusInputProps }
    } = this.props;

    this.setState({ hasFocus: true });

    if (onFocus) {
      onFocus(e);
    }

    if (onFocusInputProps) {
      onFocusInputProps(e);
    }
  }

  _handleOnBlur(e) {
    const {
      onBlur,
      inputProps: { onBlur: onBlurInputProps }
    } = this.props;

    this.setState({ hasFocus: false });

    if (onBlur) {
      onBlur(e);
    }

    if (onBlurInputProps) {
      onBlurInputProps(e);
    }
  }

  _handleOnEnter() {
    const { onEnter } = this.props;

    onEnter(this.state.value);
  }

  render() {
    const {
      disabled,
      placeholder,
      label,
      helperText,
      icons,
      inputProps,
      displayIcon,
      displayStatus,
      type,
      statusText,
      status,
      size,
      statusInfo: statusInfoProps,
      disableBorder,
      inverse
    } = this.props;

    const { hasFocus } = this.state;
    const { labelComponent, labelComponentProps } = inputProps;
    const statusInfo = inputProps ? inputProps.statusInfo : statusInfoProps;

    const classesWrap = classNames(
      styles.wrap,
      { [styles.noBorder]: disableBorder },
      { [styles.disabled]: disabled },
      { [styles.small]: size === SMALL_SIZE },
      { [styles.large]: size === LARGE_SIZE },
      { [styles.error]: !disabled && status === STATUS.ERROR },
      { [styles.warning]: !disabled && status === STATUS.WARNING },
      { [styles.active]: !disabled && status === STATUS.ACTIVE },
      { [styles.focus]: !disabled && hasFocus }
    );

    const inputClasses = classNames(
      'form-control',
      styles.input,
      { [styles.noPadding]: disableBorder },
      { [styles.inputInverse]: inverse }
    );

    const iconType = icons[status] || mapperIcons[status];
    const showIcon = Boolean(status) && !disabled && iconType.trim().length > 0;
    const showStatus = Boolean(statusText || statusInfo) && !disabled;

    return (
      <ViewContainer fluid>
        {Boolean(label) && (
          <Col size={12}>
            <InputLabel
              size={size}
              text={label}
              helperText={helperText}
              disabled={disabled}
              component={labelComponent}
              componentProps={labelComponentProps}
            />
          </Col>
        )}
        <Col size={12} className={classesWrap}>
          <input
            ref={this.textInputRef}
            type={type}
            className={inputClasses}
            placeholder={placeholder}
            value={this.state.value}
            readOnly={disabled}
            onChange={this._handleOnChange}
            onKeyUp={this._handleOnKeyUp}
            {...inputProps}
            onFocus={this._handleOnFocus}
            onBlur={this._handleOnBlur}
          />
          {Boolean(showIcon && iconType && displayIcon) ? (
            <Icon className={styles.icon} type={iconType} />
          ) : null}
        </Col>
        <Display when={displayStatus}>
          <Col size={12} className={styles.status}>
            <Display when={showStatus}>
              <InputStatus
                size={size}
                text={statusText}
                status={status}
                info={statusInfo}
                disabled={disabled}
              />
            </Display>
          </Col>
        </Display>
      </ViewContainer>
    );
  }
}

TextInput.ICON_STATUS_MAPPER = mapperIcons;
export default TextInput;
